import React, { useState, useEffect } from 'react';
import { Upload, Settings, Palette, Image, Save, RotateCcw, Plus } from 'lucide-react';
import { addFloor, getFloorData, saveFloorData, fetchSceneElements } from '../utils/api';
import { uploadToCloudinary } from '../utils/cloudinaryUpload';
import '../styles/FloorManager.css';

const FloorManager = ({ 
  collectionId, 
  currentFloorData, 
  onFloorUpdate,
  onClose,
  embedded = false // When true, renders without overlay/modal wrapper
}) => {
  const [floorData, setFloorData] = useState({
    type: 'wood',
    width: 2000,
    depth: 1500,
    height: 0,
    texture_url: null,
    material: 'wood',
    color: '#8B4513',
    roughness: 0.7,
    metalness: 0.0,
    tile_size: 24,
    grout_color: '#654321',
    visible: true,
    cast_shadows: true,
    receive_shadows: true
  });
  
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  
  // Load existing floor data on mount
  useEffect(() => {
    const loadFloorData = async () => {
      if (collectionId) {
        try {
          setIsLoading(true);
          console.log('Loading floor data for collection:', collectionId);
          
          const existingFloor = await getFloorData(collectionId);
          if (existingFloor) {
            console.log('Floor data loaded:', existingFloor);
            console.log('Floor ID fields:', { 
              id: existingFloor.id, 
              element_id: existingFloor.element_id,
              hasId: !!existingFloor.id,
              hasElementId: !!existingFloor.element_id,
              primaryKey: existingFloor.element_id || 'none'
            });
            
            // Ensure we have an id field for proper detection
            const floorWithId = {
              ...existingFloor,
              id: existingFloor.element_id || existingFloor.id || 'existing-floor'
            };
            console.log('Floor with ID:', floorWithId);
            setFloorData(floorWithId);
            if (onFloorUpdate) {
              onFloorUpdate(floorWithId);
            }
          } else {
            console.log('No floor data found');
          }
        } catch (error) {
          console.error('Error loading floor data:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };
    
    loadFloorData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionId]); // Only reload when collectionId changes, not when onFloorUpdate reference changes
  
  // Initialize with current data if provided
  useEffect(() => {
    if (currentFloorData && !isLoading) {
      setFloorData(prev => ({ ...prev, ...currentFloorData }));
    }
  }, [currentFloorData, isLoading]);
  
  // Material presets
  const materialPresets = {
    wood: {
      name: 'Wood',
      color: '#8B4513',
      roughness: 0.7,
      metalness: 0.0,
      tile_size: 24,
      grout_color: '#654321'
    },
    concrete: {
      name: 'Concrete',
      color: '#C0C0C0',
      roughness: 0.9,
      metalness: 0.0,
      tile_size: 48,
      grout_color: '#A0A0A0'
    },
    tile: {
      name: 'Ceramic Tile',
      color: '#F5F5DC',
      roughness: 0.3,
      metalness: 0.1,
      tile_size: 12,
      grout_color: '#696969'
    },
    marble: {
      name: 'Marble',
      color: '#F5F5F5',
      roughness: 0.2,
      metalness: 0.1,
      tile_size: 96,
      grout_color: '#D3D3D3'
    },
    stone: {
      name: 'Stone',
      color: '#808080',
      roughness: 0.8,
      metalness: 0.0,
      tile_size: 36,
      grout_color: '#696969'
    }
  };
  
  // Handle material preset selection
  const handleMaterialChange = (materialType) => {
    const preset = materialPresets[materialType];
    setFloorData({
      ...floorData,
      material: materialType,
      color: preset.color,
      roughness: preset.roughness,
      metalness: preset.metalness,
      tile_size: preset.tile_size,
      grout_color: preset.grout_color
    });
  };
  
  // Handle texture upload
  const handleTextureUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Validate file
    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      alert('File size must be less than 5MB');
      return;
    }
    
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file (JPG, PNG, WebP)');
      return;
    }
    
    setIsUploading(true);
    setUploadProgress(0);
    
    try {
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 80) {
            clearInterval(progressInterval);
            return 80;
          }
          return prev + 10;
        });
      }, 200);
      
      // Upload to Cloudinary using your existing function
      const uploadResult = await uploadToCloudinary(
        file, 
        'image', 
        'floor_textures', // Store in floor_textures folder
        `floor_${collectionId}_${Date.now()}` // Unique public ID
      );
      
      // Update floor data with the uploaded texture
      setFloorData(prev => ({
        ...prev,
        texture_url: uploadResult.url,
        material: 'custom'
      }));
      
      setUploadProgress(100);
      
      // Show success message
      console.log('Texture uploaded successfully:', uploadResult);
      
      // Reset progress after showing completion
      setTimeout(() => setUploadProgress(0), 1000);
      
    } catch (error) {
      console.error('Texture upload failed:', error);
      alert(`Upload failed: ${error.message}`);
      setUploadProgress(0);
    } finally {
      setIsUploading(false);
    }
  };
  
  // Create floor using the simple addFloor function
  const handleCreateFloor = async () => {
    if (!collectionId) {
      alert('No collection ID found. Cannot create floor.');
      return;
    }
    
    try {
      setIsCreating(true);
      
      // Use the simple addFloor function (like addWall)
      const result = await addFloor(collectionId, floorData.width, floorData.depth, floorData.height);
      
      if (result && result.floor) {
        console.log('Floor created successfully:', result.floor);
        
        // Update local state with the created floor data
        const newFloorData = {
          ...floorData,
          id: result.floor.element_id || result.floor.id
        };
        
        setFloorData(newFloorData);
        
        // Update parent component
        if (onFloorUpdate) {
          onFloorUpdate(newFloorData);
        }
        
        // Show success message
        alert('Floor created successfully! You can now customize it.');
      } else {
        throw new Error('No floor data returned from creation');
      }
      
    } catch (error) {
      console.error('Error creating floor:', error);
      alert(`Error creating floor: ${error.message}`);
    } finally {
      setIsCreating(false);
    }
  };
  
  // Reset to defaults
  const handleReset = () => {
    setFloorData({
      type: 'wood',
      width: 2000,
      depth: 1500,
      height: 0,
      texture_url: null,
      material: 'wood',
      color: '#8B4513',
      roughness: 0.7,
      metalness: 0.0,
      tile_size: 24,
      grout_color: '#654321',
      visible: true,
      cast_shadows: true,
      receive_shadows: true
    });
  };
  
  // Remove custom texture
  const handleRemoveTexture = () => {
    setFloorData(prev => ({
      ...prev,
      texture_url: null,
      material: 'wood'
    }));
  };

  // Save floor changes to database
  const handleSaveFloor = async () => {
    if (!floorData.id) {
      console.log('No floor ID to save');
      return;
    }

    // If it's an existing floor without a real database ID, we need to handle it differently
    if (floorData.id === 'existing-floor') {
      console.log('Saving existing floor data...');
      // For existing floors, we'll try to update the scene_elements record
      try {
        setIsSaving(true);
        console.log('Saving existing floor data:', floorData);
        
        // Since this is an existing floor, we need to find and update it
        const result = await saveFloorData(collectionId, floorData);
        
        if (result) {
          console.log('Floor saved successfully:', result);
          alert('Floor settings saved successfully!');
          
          // Update parent component
          if (onFloorUpdate) {
            onFloorUpdate(floorData);
          }
        } else {
          throw new Error('Failed to save floor data');
        }
      } catch (error) {
        console.error('Error saving existing floor:', error);
        alert(`Error saving floor: ${error.message}`);
      } finally {
        setIsSaving(false);
      }
      return;
    }

    try {
      setIsSaving(true);
      console.log('Saving floor data:', floorData);
      
      // Call the API to save floor data
      const result = await saveFloorData(collectionId, floorData);
      
      if (result) {
        console.log('Floor saved successfully:', result);
        alert('Floor settings saved successfully!');
        
        // Update parent component
        if (onFloorUpdate) {
          onFloorUpdate(floorData);
        }
      } else {
        throw new Error('Failed to save floor data');
      }
    } catch (error) {
      console.error('Error saving floor:', error);
      alert(`Error saving floor: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };
  
  if (isLoading) {
    if (embedded) {
      return (
        <div className="floor-manager-content">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading floor settings...</p>
          </div>
        </div>
      );
    }
    return (
      <div className="floor-manager-overlay">
        <div className="floor-manager-modal">
          <div className="floor-manager-content">
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading floor settings...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Debug logging
  console.log('FloorManager render - floorData:', floorData);
  console.log('FloorManager render - floorData.id:', floorData.id);
  console.log('FloorManager render - should show save button:', !!floorData.id);
  
  // Content that's shared between embedded and standalone modes
  const floorContent = (
    <>
      <div className="floor-manager-content">
        {/* Initial Floor Creation */}
          {(!floorData.id || floorData.id === 'existing-floor') && (
            <div className="section">
              <div className="info-box">
                <h3>🎯 Floor Status</h3>
                {floorData.id === 'existing-floor' ? (
                  <p>✅ Floor detected! You can customize it below.</p>
                ) : (
                  <p>This collection doesn't have a floor yet. Create a floor to get started!</p>
                )}
                <div className="constraint-note">
                  <small>⚠️ Note: Database constraints limit floor dimensions to 999 inches maximum.</small>
                </div>
                {!floorData.id && (
                  <button 
                    onClick={handleCreateFloor}
                    className="create-floor-btn"
                    disabled={isCreating}
                  >
                    {isCreating ? (
                      <>
                        <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                        Creating Floor...
                      </>
                    ) : (
                      <>
                        <Plus size={16} />
                        Create Floor
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          )}
          
          {/* Material Presets */}
          <div className="section">
            <h3>Material Presets</h3>
            <div className="material-presets">
              {Object.entries(materialPresets).map(([key, preset]) => (
                <button
                  key={key}
                  className={`material-preset ${floorData.material === key ? 'active' : ''}`}
                  onClick={() => handleMaterialChange(key)}
                  style={{ backgroundColor: preset.color }}
                >
                  <span className="preset-name">{preset.name}</span>
                </button>
              ))}
            </div>
          </div>
          
          {/* Custom Texture Upload */}
          <div className="section">
            <h3>Custom Texture</h3>
            <div className="texture-upload">
              {floorData.texture_url ? (
                <div className="current-texture">
                  <img 
                    src={floorData.texture_url} 
                    alt="Floor texture" 
                    className="texture-preview"
                  />
                  <div className="texture-info">
                    <p>Custom texture loaded</p>
                    <p className="texture-url">{floorData.texture_url}</p>
                    <button 
                      onClick={handleRemoveTexture}
                      className="remove-texture-btn"
                    >
                      Remove Texture
                    </button>
                  </div>
                </div>
              ) : (
                <div className="upload-area">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleTextureUpload}
                    id="texture-upload"
                    className="file-input"
                  />
                  <label htmlFor="texture-upload" className="upload-label">
                    <Upload size={24} />
                    <span>Upload Floor Texture</span>
                    <small>JPG, PNG, or WebP (max 5MB)</small>
                  </label>
                </div>
              )}
              
              {isUploading && (
                <div className="upload-progress">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <span>{uploadProgress}%</span>
                </div>
              )}
            </div>
          </div>
          
          {/* Basic Properties */}
          <div className="section">
            <h3>Basic Properties</h3>
            <button
              type="button"
              onClick={async () => {
                try {
                  const elements = await fetchSceneElements(collectionId);
                  const walls = elements.filter(e => e.type === 'wall_2d');
                  if (walls.length === 0) return;
                  let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
                  walls.forEach(w => {
                    const cx = parseFloat(w.wall_center_x) || 0;
                    const cz = parseFloat(w.wall_center_z) || 0;
                    const hw = (parseFloat(w.width) || 0) / 2;
                    const rotRad = ((parseFloat(w.wall_rotation_y) || 0) * Math.PI) / 180;
                    const cosR = Math.cos(rotRad), sinR = Math.sin(rotRad);
                    [[-hw, -1], [hw, -1], [hw, 1], [-hw, 1]].forEach(([lx, lz]) => {
                      const rx = lx * cosR - lz * sinR + cx;
                      const rz = lx * sinR + lz * cosR + cz;
                      minX = Math.min(minX, rx); maxX = Math.max(maxX, rx);
                      minZ = Math.min(minZ, rz); maxZ = Math.max(maxZ, rz);
                    });
                  });
                  const pad = 60; // 5 feet padding on each side
                  const newWidth = Math.ceil((maxX - minX + pad * 2) / 12) * 12;
                  const newDepth = Math.ceil((maxZ - minZ + pad * 2) / 12) * 12;
                  const centerX = Math.round((minX + maxX) / 2);
                  const centerZ = Math.round((minZ + maxZ) / 2);
                  setFloorData(prev => ({ ...prev, width: newWidth, depth: newDepth, position_x: centerX, position_z: centerZ }));
                } catch (err) {
                  console.error('Auto-size error:', err);
                }
              }}
              style={{
                width: '100%', padding: '8px', marginBottom: '12px',
                border: '1px solid #6366f1', borderRadius: '6px',
                background: '#eef2ff', color: '#4338ca', cursor: 'pointer',
                fontSize: '13px', fontWeight: 600
              }}
            >
              📏 Auto-size from walls
            </button>
            <div className="property-grid">
              <div className="property-group">
                <label>Width (inches)</label>
                <input
                  type="number"
                  value={floorData.width}
                  onChange={(e) => setFloorData(prev => ({
                    ...prev,
                    width: parseInt(e.target.value) || 2000
                  }))}
                  min="100"
                  max="10000"
                  step="12"
                />
                <small>Gallery width in inches</small>
              </div>
              
              <div className="property-group">
                <label>Depth (inches)</label>
                <input
                  type="number"
                  value={floorData.depth}
                  onChange={(e) => setFloorData(prev => ({
                    ...prev,
                    depth: parseInt(e.target.value) || 1500
                  }))}
                  min="100"
                  max="10000"
                  step="12"
                />
                <small>Gallery depth in inches</small>
              </div>
              
              <div className="property-group">
                <label>Height (inches)</label>
                <input
                  type="number"
                  value={floorData.height}
                  onChange={(e) => setFloorData(prev => ({
                    ...prev,
                    height: parseInt(e.target.value) || 0
                  }))}
                  min="-100"
                  max="100"
                  step="1"
                />
                <small>Floor height relative to walls</small>
              </div>
              
              <div className="property-group">
                <label>Center X (inches)</label>
                <input
                  type="number"
                  value={floorData.position_x || 0}
                  onChange={(e) => setFloorData(prev => ({
                    ...prev,
                    position_x: parseInt(e.target.value) || 0
                  }))}
                  step="12"
                />
                <small>Floor center X position</small>
              </div>
              
              <div className="property-group">
                <label>Center Z (inches)</label>
                <input
                  type="number"
                  value={floorData.position_z || 0}
                  onChange={(e) => setFloorData(prev => ({
                    ...prev,
                    position_z: parseInt(e.target.value) || 0
                  }))}
                  step="12"
                />
                <small>Floor center Z position</small>
              </div>
              
              <div className="property-group">
                <label>Tile Size (inches)</label>
                <input
                  type="number"
                  value={floorData.tile_size}
                  onChange={(e) => setFloorData(prev => ({
                    ...prev,
                    tile_size: parseInt(e.target.value) || 24
                  }))}
                  min="1"
                  max="200"
                  step="1"
                />
                <small>Size of floor tiles/planks</small>
              </div>
            </div>
          </div>
          
          {/* Advanced Properties */}
          <div className="section">
            <button
              className="advanced-toggle"
              onClick={() => setShowAdvanced(!showAdvanced)}
            >
              <Settings size={16} />
              {showAdvanced ? 'Hide' : 'Show'} Advanced Properties
            </button>
            
            {showAdvanced && (
              <div className="advanced-properties">
                <div className="property-grid">
                  <div className="property-group">
                    <label>Base Color</label>
                    <input
                      type="color"
                      value={floorData.color}
                      onChange={(e) => setFloorData(prev => ({
                        ...prev,
                        color: e.target.value
                      }))}
                    />
                    <small>Primary floor color</small>
                  </div>
                  
                  <div className="property-group">
                    <label>Grout Color</label>
                    <input
                      type="color"
                      value={floorData.grout_color}
                      onChange={(e) => setFloorData(prev => ({
                        ...prev,
                        grout_color: e.target.value
                      }))}
                    />
                    <small>Color between tiles</small>
                  </div>
                  
                  <div className="property-group">
                    <label>Roughness</label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={floorData.roughness}
                      onChange={(e) => setFloorData(prev => ({
                        ...prev,
                        roughness: parseFloat(e.target.value)
                      }))}
                    />
                    <small>Surface roughness (0 = smooth, 1 = rough)</small>
                  </div>
                  
                  <div className="property-group">
                    <label>Metalness</label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={floorData.metalness}
                      onChange={(e) => setFloorData(prev => ({
                        ...prev,
                        metalness: parseFloat(e.target.value)
                      }))}
                    />
                    <small>Metallic appearance (0 = matte, 1 = metallic)</small>
                  </div>
                </div>
                
                <div className="checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={floorData.visible}
                      onChange={(e) => setFloorData(prev => ({
                        ...prev,
                        visible: e.target.checked
                      }))}
                    />
                    Floor Visible
                  </label>
                  
                  <label>
                    <input
                      type="checkbox"
                      checked={floorData.cast_shadows}
                      onChange={(e) => setFloorData(prev => ({
                        ...prev,
                        cast_shadows: e.target.checked
                      }))}
                    />
                    Cast Shadows
                  </label>
                  
                  <label>
                    <input
                      type="checkbox"
                      checked={floorData.receive_shadows}
                      onChange={(e) => setFloorData(prev => ({
                        ...prev,
                        receive_shadows: e.target.checked
                      }))}
                    />
                    Receive Shadows
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="floor-manager-actions">
          <button onClick={handleReset} className="reset-btn">
            <RotateCcw size={16} />
            Reset to Defaults
          </button>
          {floorData.id && (
            <>
              <button 
                onClick={handleSaveFloor} 
                className="save-btn primary"
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Save Changes
                  </>
                )}
              </button>
              {!embedded && (
                <button 
                  onClick={onClose} 
                  className="close-btn secondary"
                >
                  Close
                </button>
              )}
            </>
          )}
          {!floorData.id && !embedded && (
            <button 
              onClick={onClose} 
              className="close-btn"
            >
              Close
            </button>
          )}
        </div>
      </>
    );

  // Embedded mode: just return the content without overlay
  if (embedded) {
    return (
      <div className="floor-manager-embedded">
        {floorContent}
      </div>
    );
  }

  // Standalone mode: wrap in overlay and modal
  return (
    <div className="floor-manager-overlay">
      <div className="floor-manager-modal">
        <div className="floor-manager-header">
          <h2>Floor Manager</h2>
          <p>Add and customize your gallery floor</p>
          <button onClick={onClose} className="close-btn">×</button>
        </div>
        {floorContent}
      </div>
    </div>
  );
};

export default FloorManager;
